/**
 * Feature E06-F04 — Library & History typed api-client + Query hooks.
 *
 * Feature-owned (NOT in the shared queries.ts / queryKeys.ts). Uses the shared
 * `apiClient` (auto-injects the session Bearer token, normalizes the response,
 * never throws) + `unwrap` (the single throw-at-the-Query-boundary helper).
 * Contracts come from `@eer/shared` so the SPA speaks the exact same shapes as
 * the Fastify API.
 *
 * Endpoints (routes/research-library.ts, mounted under /research by the Wire pass):
 *   GET  /research/list?scope=library&q=&industry=&approved_only=&page=&page_size=  (T01/T02/T03/T07)
 *   GET  /research/list?scope=history&q=&status=&page=&page_size=     (T01/T04)
 *   POST /research/list/clone   { sourceRequestId }                   (T05)
 *   POST /research/list/similar { industry, subCategories, locations, … } (E06-F06-T04)
 *
 * Each list response is validated against the shared schema at the boundary so a
 * drifted shape fails loudly rather than rendering garbage.
 */
import {
  useQuery,
  useMutation,
  keepPreviousData,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  libraryListResponseSchema,
  historyListResponseSchema,
  cloneResponseSchema,
  similarListsResponseSchema,
  RESEARCH_LIST_PAGE_SIZE,
  type LibraryListResponse,
  type HistoryListResponse,
  type CloneResponse,
  type ResearchRequestStatus,
  type SimilarListsRequest,
  type SimilarListsResponse,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

// ---------------------------------------------------------------------------
// query keys (feature-local — the shared registry has no research domain)
// ---------------------------------------------------------------------------

/** Library list filter (server-side search/filter/pagination). */
export interface LibraryListFilter {
  readonly q?: string;
  readonly industry?: string;
  readonly page?: number;
  /** E06-F04-T07 — "Approved only" mode (companies approved in a submitted review). */
  readonly approvedOnly?: boolean;
}

/** History list filter. */
export interface HistoryListFilter {
  readonly q?: string;
  readonly status?: ResearchRequestStatus;
  readonly page?: number;
}

export const researchKeys = {
  all: ['research'] as const,
  library: (filter: LibraryListFilter) => ['research', 'library', filter] as const,
  history: (filter: HistoryListFilter) => ['research', 'history', filter] as const,
  similar: (input: SimilarListsRequest) => ['research', 'similar', input] as const,
} as const;

// ---------------------------------------------------------------------------
// query params
// ---------------------------------------------------------------------------

function libraryParams(filter: LibraryListFilter): Record<string, string | number> {
  const params: Record<string, string | number> = {
    scope: 'library',
    page: filter.page ?? 1,
    page_size: RESEARCH_LIST_PAGE_SIZE,
  };
  if (filter.q && filter.q.trim()) params.q = filter.q.trim();
  if (filter.industry && filter.industry !== 'all') params.industry = filter.industry;
  if (filter.approvedOnly) params.approved_only = 'true';
  return params;
}

function historyParams(filter: HistoryListFilter): Record<string, string | number> {
  const params: Record<string, string | number> = {
    scope: 'history',
    page: filter.page ?? 1,
    page_size: RESEARCH_LIST_PAGE_SIZE,
  };
  if (filter.q && filter.q.trim()) params.q = filter.q.trim();
  if (filter.status) params.status = filter.status;
  return params;
}

// ---------------------------------------------------------------------------
// fetchers
// ---------------------------------------------------------------------------

/** Fetch one Library page (companies + stat-cards), validated at the boundary. */
export async function fetchLibrary(filter: LibraryListFilter): Promise<LibraryListResponse> {
  const result = await apiClient.get<unknown>('/research/list', {
    command: 'research_list',
    query: libraryParams(filter),
  });
  return libraryListResponseSchema.parse(unwrap(result));
}

/** Fetch one History page (past runs), validated at the boundary. */
export async function fetchHistory(filter: HistoryListFilter): Promise<HistoryListResponse> {
  const result = await apiClient.get<unknown>('/research/list', {
    command: 'research_list',
    query: historyParams(filter),
  });
  return historyListResponseSchema.parse(unwrap(result));
}

/**
 * Copy a past run's criteria for an Intake prefill (T05). Read-only since
 * E12-F17-T04: the server writes no row; Run / Save draft creates the request.
 */
export async function cloneResearchRequest(sourceRequestId: number): Promise<CloneResponse> {
  const result = await apiClient.post<unknown>(
    '/research/list/clone',
    { sourceRequestId },
    { command: 'research_clone' },
  );
  return cloneResponseSchema.parse(unwrap(result));
}

/**
 * E06-F06-T04 — past lists similar to the Intake draft's criteria (up to 5, scored
 * on the server). Validated at the boundary like the list fetchers.
 */
export async function fetchSimilarLists(input: SimilarListsRequest): Promise<SimilarListsResponse> {
  const result = await apiClient.post<unknown>('/research/list/similar', input, {
    command: 'research_list_similar',
  });
  return similarListsResponseSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

/** Library browser query (T01/T02/T03). Keeps the previous page while paging. */
export function useResearchLibrary(
  filter: LibraryListFilter,
  enabled: boolean = true,
): UseQueryResult<LibraryListResponse, ApiResultError> {
  return useQuery<LibraryListResponse, ApiResultError>({
    queryKey: researchKeys.library(filter),
    queryFn: () => fetchLibrary(filter),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** History browser query (T01/T04). Keeps the previous page while paging. */
export function useResearchHistory(
  filter: HistoryListFilter,
  enabled: boolean = true,
): UseQueryResult<HistoryListResponse, ApiResultError> {
  return useQuery<HistoryListResponse, ApiResultError>({
    queryKey: researchKeys.history(filter),
    queryFn: () => fetchHistory(filter),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * Criteria-copy mutation (T05; read-only since E12-F17-T04). On success the caller
 * fills the Intake form from `res.prefill` (criteria + '<title> (copy)'). Nothing is
 * persisted, so no research query is invalidated — History is unchanged until the
 * recruiter presses Run or Save draft.
 */
export function useCloneResearchRequest(): UseMutationResult<
  CloneResponse,
  ApiResultError,
  number
> {
  return useMutation<CloneResponse, ApiResultError, number>({
    mutationFn: (sourceRequestId) => cloneResearchRequest(sourceRequestId),
  });
}

/**
 * E06-F06-T04 — «Похожие списки» hint query. The caller debounces `input` and gates
 * `enabled` (Targeting filled). No retry: a failing hint must never nag the form —
 * the panel simply stays hidden (spec: «ошибка подсказки не мешает заполнять»).
 */
export function useSimilarLists(
  input: SimilarListsRequest,
  enabled: boolean,
): UseQueryResult<SimilarListsResponse, ApiResultError> {
  return useQuery<SimilarListsResponse, ApiResultError>({
    queryKey: researchKeys.similar(input),
    queryFn: () => fetchSimilarLists(input),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}
